import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { meetingsApi, contactsApi } from "../../services/api";
import { useBuilding } from "../../context/BuildingContext";
import type {
  Meeting,
  MeetingAttachment,
  MeetingTranscriptSegment,
} from "@akfdev-com/shared/src/models/Meeting";
import type { Contact } from "@akfdev-com/shared/src/models/Contact";

declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const MeetingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBuilding } = useBuilding();

  const routeBuildingId = (location.state as { buildingId?: string } | null)
    ?.buildingId;

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);

  const effectiveBuildingId =
    currentBuilding && currentBuilding.id !== "__ALL__"
      ? currentBuilding.id
      : routeBuildingId;

  useEffect(() => {
    const load = async () => {
      if (!id || !effectiveBuildingId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const [meetingData, contactsData] = await Promise.all([
          meetingsApi.get(id, effectiveBuildingId),
          contactsApi.getAll(),
        ]);
        setMeeting(meetingData);
        setContacts(contactsData || []);
      } catch {
        setError("Failed to load meeting details.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, effectiveBuildingId]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current && isRecordingRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleSave = async (updates: Partial<Meeting>) => {
    if (!meeting || !effectiveBuildingId) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updated = await meetingsApi.update(meeting.id, {
        ...updates,
        buildingId: effectiveBuildingId,
      });
      setMeeting(updated);
    } catch {
      setError("Failed to save meeting changes.");
    } finally {
      setSaving(false);
    }
  };

  const startMeetingDictation = async () => {
    if (!meeting) {
      return;
    }

    // Request microphone access with audio constraints for better quality
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      setError(
        "Microphone access denied or unavailable. Please allow microphone access to start recording.",
      );
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: any) => {
      let combined = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        combined += `${event.results[i][0].transcript} `;
      }

      setMeeting((prev) => {
        if (!prev) {
          return prev;
        }

        const segment: MeetingTranscriptSegment = {
          id: crypto.randomUUID(),
          speakerLabel: "Speaker A",
          text: combined.trim(),
          createdAt: new Date().toISOString(),
        };

        return {
          ...prev,
          transcriptText: `${prev.transcriptText} ${combined}`.trim(),
          transcriptSegments: [...prev.transcriptSegments, segment],
        };
      });
    };

    recognition.onerror = () => {
      setError("Dictation stopped due to a microphone or recognition error.");
      isRecordingRef.current = false;
      setMeeting((prev) =>
        prev
          ? {
              ...prev,
              recordingStatus: "failed",
              recordingEndedAt: new Date().toISOString(),
            }
          : prev,
      );
    };

    recognition.onend = () => {
      isRecordingRef.current = false;
    };

    recognitionRef.current = recognition;
    isRecordingRef.current = true;
    recognition.start();

    await handleSave({
      recordingStatus: "recording",
      recordingStartedAt: new Date().toISOString(),
      recordingEndedAt: undefined,
    });
  };

  const stopMeetingDictation = async () => {
    if (!meeting) {
      return;
    }

    if (recognitionRef.current && isRecordingRef.current) {
      recognitionRef.current.stop();
      isRecordingRef.current = false;
    }

    await handleSave({
      recordingStatus: "completed",
      recordingEndedAt: new Date().toISOString(),
      transcriptText: meeting.transcriptText,
      transcriptSegments: meeting.transcriptSegments,
    });
  };

  const handleAttachmentSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!meeting || !event.target.files?.length) {
      return;
    }

    setUploading(true);
    setError(null);
    const nextAttachments: MeetingAttachment[] = [...meeting.attachments];

    for (const file of Array.from(event.target.files)) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError(`Unsupported file type: ${file.name}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`${file.name} exceeds the 25MB limit.`);
        continue;
      }

      try {
        const { url, key } = await meetingsApi.getUploadUrl(
          meeting.id,
          file.name,
          file.type,
        );

        await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });

        nextAttachments.push({
          id: crypto.randomUUID(),
          fileName: file.name,
          key,
          contentType: file.type,
          sizeBytes: file.size,
          uploadedAt: new Date().toISOString(),
          uploadedBy: "current-user",
        });
      } catch {
        setError(`Failed to upload ${file.name}.`);
      }
    }

    await handleSave({ attachments: nextAttachments });
    setUploading(false);
    event.target.value = "";
  };

  const handleDownload = async (attachment: MeetingAttachment) => {
    if (!meeting) return;
    try {
      const { url } = await meetingsApi.getDownloadUrl(
        meeting.id,
        attachment.key,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError(`Failed to get download link for ${attachment.fileName}.`);
    }
  };

  if (loading) {
    return <Spinner message="Loading meeting..." />;
  }

  if (error && !meeting) {
    return <ErrorAlert message={error} />;
  }

  if (!meeting || !effectiveBuildingId) {
    return (
      <ErrorAlert message="Meeting not available. Please select a building and try again." />
    );
  }

  return (
    <div className="">
      <div className="mb-4">
        <Link
          to="/dashboard/meetings"
          className="text-sm text-gray-500 hover:text-primary-600"
        >
          Back to Meetings
        </Link>
      </div>

      <PageHeader
        title={meeting.title}
        description={`${meeting.date}${
          meeting.location ? ` · ${meeting.location}` : ""
        }`}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={startMeetingDictation}
              disabled={saving || meeting.recordingStatus === "recording"}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              Start Meeting
            </button>
            <button
              type="button"
              onClick={stopMeetingDictation}
              disabled={saving || meeting.recordingStatus !== "recording"}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              Stop Recording
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/meetings")}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white shadow sm:rounded-lg p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              Meeting Setup
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Chairperson
                </label>
                <select
                  value={meeting.chairpersonContactId}
                  onChange={(e) =>
                    handleSave({ chairpersonContactId: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="">Select chairperson</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Recording Status
                </label>
                <p className="mt-2 text-sm text-gray-900 capitalize">
                  {meeting.recordingStatus}
                </p>
                <p className="text-xs text-gray-500">
                  Recording is mandatory for all meetings.
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Attendees
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {contacts.map((contact) => {
                  const checked = meeting.attendeeContactIds.includes(
                    contact.id,
                  );
                  return (
                    <label
                      key={contact.id}
                      className="inline-flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...meeting.attendeeContactIds, contact.id]
                            : meeting.attendeeContactIds.filter(
                                (idValue) => idValue !== contact.id,
                              );
                          handleSave({ attendeeContactIds: next });
                        }}
                        className="rounded border-gray-300 text-primary-600"
                      />
                      {contact.name}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                rows={4}
                value={meeting.notes || ""}
                onChange={(e) =>
                  setMeeting((prev) =>
                    prev ? { ...prev, notes: e.target.value } : prev,
                  )
                }
                onBlur={() => handleSave({ notes: meeting.notes || "" })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                placeholder="Meeting notes and resolutions"
              />
            </div>
          </div>

          <div className="bg-white shadow sm:rounded-lg p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              Live Transcript
            </h2>
            <p className="text-sm text-gray-600">
              Real-time transcript accumulates while recording is active.
              Speaker diarization is currently a placeholder label and will be
              upgraded when streaming transcription integration is connected.
            </p>
            <textarea
              rows={10}
              value={meeting.transcriptText}
              onChange={(e) =>
                setMeeting((prev) =>
                  prev ? { ...prev, transcriptText: e.target.value } : prev,
                )
              }
              onBlur={() =>
                handleSave({ transcriptText: meeting.transcriptText })
              }
              className="block w-full rounded-md border-gray-300 shadow-sm font-mono text-sm"
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white shadow sm:rounded-lg p-5 space-y-3">
            <h2 className="text-base font-semibold text-gray-900">
              Attachments
            </h2>
            <p className="text-xs text-gray-500">
              PDF, DOC and DOCX only. Max file size: 25MB.
            </p>
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              onChange={handleAttachmentSelection}
              disabled={uploading}
              className="block w-full text-sm text-gray-600"
            />
            {uploading && <p className="text-xs text-primary-600">Uploading...</p>}
            <ul className="space-y-2">
              {meeting.attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="text-sm text-gray-700 border rounded px-2 py-1 flex items-center justify-between gap-2"
                >
                  <div>
                    <p className="font-medium">{attachment.fileName}</p>
                    <p className="text-xs text-gray-500">
                      {(attachment.sizeBytes / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="text-primary-600 hover:text-primary-800 text-xs px-2 py-1 border rounded hover:bg-primary-50"
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white shadow sm:rounded-lg p-5 space-y-3">
            <h2 className="text-base font-semibold text-gray-900">
              Regulatory Schedule
            </h2>
            <p className="text-sm text-gray-700">
              Category:{" "}
              <span className="font-medium capitalize">
                {meeting.regulatorySchedule.category}
              </span>
            </p>
            <p className="text-sm text-gray-700">
              Source:{" "}
              <span className="font-medium capitalize">
                {meeting.regulatorySchedule.source}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              UK defaults and extra date management will be added in the next
              implementation slice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetails;
